import { useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { HomeMobilePromo } from '@/features/home/components/HomeMobilePromo'
import { useSpProfile } from '@/features/rewards/api/profile-queries'
import { AccountDetailsTab } from '@/features/account/components/AccountDetailsTab'
import { AccountPageSkeleton } from '@/features/account/components/AccountPageSkeleton'
import { AccountTabBar, type AccountTab } from '@/features/account/components/AccountTabBar'
import { ChangePasswordTab } from '@/features/account/components/ChangePasswordTab'
import type { AccountEditingGuard } from '@/features/account/lib/account-editing-guard'
import {
  homeContentInnerSx,
  homeContentShellSx,
  homeHeroGreetingSx,
  homeHeroWelcomeSx,
  providerShellHeroShellSx,
  providerShellHeroSx,
} from '@/features/home/styles/homeStyles'
import {
  ACCOUNT_PAGE_MAX_WIDTH,
  accountPageSubtitleSx,
  accountPageTitleSx,
  accountTabSectionSx,
} from '@/features/account/lib/account-settings-tokens'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useSessionStore } from '@/stores/useSessionStore'

function greetingFirstName(displayName: string | undefined): string {
  const part = displayName?.trim().split(/\s+/)[0]
  return part || 'there'
}

export function AccountPage() {
  useDocumentTitle('Account')
  const [tab, setTab] = useState<AccountTab>('details')
  const editingGuardRef = useRef<AccountEditingGuard | null>(null)

  const handleTabChange = (next: AccountTab) => {
    if (next !== tab && editingGuardRef.current?.isBlocking) {
      editingGuardRef.current.requestLeave(() => setTab(next))
      return
    }
    setTab(next)
  }
  const userName = useSessionStore((s) => s.user?.name)
  const firstName = greetingFirstName(userName)
  const profileQuery = useSpProfile()

  if (profileQuery.isLoading || !profileQuery.data) {
    return <AccountPageSkeleton />
  }

  const profile = profileQuery.data

  return (
    <Box data-testid="account-page">
      <Box sx={providerShellHeroSx}>
        <Box sx={providerShellHeroShellSx}>
          <Box sx={homeContentInnerSx}>
            <Typography component="h1" sx={homeHeroGreetingSx}>
              Hey, {firstName}!
            </Typography>
            <Typography sx={homeHeroWelcomeSx}>Welcome to Brushfire</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={homeContentShellSx}>
        <Box sx={{ ...homeContentInnerSx, maxWidth: ACCOUNT_PAGE_MAX_WIDTH }}>
          <Typography sx={accountPageTitleSx}>Account</Typography>
          <Typography sx={accountPageSubtitleSx}>
            View your account and password details.
          </Typography>

          <AccountTabBar activeTab={tab} onTabChange={handleTabChange} />

          <Box
            sx={accountTabSectionSx}
            role="tabpanel"
            aria-labelledby={tab === 'details' ? 'account-tab-details' : 'account-tab-password'}
          >
            {tab === 'details' ? (
              <AccountDetailsTab
                profile={profile}
                onEditingGuardChange={(guard) => {
                  editingGuardRef.current = guard
                }}
              />
            ) : (
              <ChangePasswordTab />
            )}
          </Box>

          <HomeMobilePromo />
        </Box>
      </Box>

    </Box>
  )
}
